/*
  Inline video.

  Replaces Dawn's click-to-load poster for native Shopify video in the product
  gallery, and drives the clips in the masonry showcase. The clip plays muted the moment it scrolls into view, and a click
  anywhere on it toggles play and pause rather than opening the media modal.

  Autoplay is deliberately tied to visibility instead of firing on page load:
  a page can hold several clips, and playing every one of them at once for
  media the shopper has not scrolled to yet is a waste of their data.
*/
if (!customElements.get('inline-video')) {
  customElements.define(
    'inline-video',
    class InlineVideo extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video) return;

        /*
          Set as properties, not just markup attributes. iOS refuses inline
          autoplay unless both muted and playsinline are true on the element
          itself, and it reads the property rather than a late attribute.
        */
        this.video.muted = true;
        this.video.playsInline = true;
        this.video.setAttribute('playsinline', '');

        // Set once the shopper pauses by hand, so scrolling away and back does
        // not override the choice they just made.
        this.pausedByUser = false;

        this.toggle = this.querySelector('.inline-video__toggle');

        this.video.addEventListener('play', () => this.reflectState(true));
        this.video.addEventListener('pause', () => this.reflectState(false));
        this.addEventListener('click', this.onClick.bind(this));

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                if (!this.pausedByUser) this.play();
              } else {
                this.video.pause();
              }
            });
          },
          { threshold: 0.5 }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      // Driven off the video's own events rather than set alongside each call,
      // so the badge stays honest when something else pauses us, such as
      // pauseAllMedia() when a modal or the cart drawer opens.
      reflectState(playing) {
        this.toggleAttribute('playing', playing);
        if (this.toggle) this.toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
      }

      play() {
        if (!this.video) return;
        this.pausedByUser = false;
        // A rejected play promise is normal, the browser blocks it while the
        // tab is hidden or a gesture is still pending. Nothing to recover from.
        const played = this.video.play();
        if (played) played.catch(() => {});
      }

      pause() {
        if (!this.video) return;
        this.pausedByUser = true;
        this.video.pause();
      }

      onClick(event) {
        event.preventDefault();
        if (this.video.paused) {
          this.play();
        } else {
          this.pause();
        }
      }
    }
  );
}
